import { useState } from "react";

import {
  useCreateTaxonomyEntry,
  useDeactivateTaxonomyEntry,
  usePurgeStack,
  useTaxonomy,
  useUpdateTaxonomyEntry,
  type TaxonomyEntryOut,
  type TaxonomyKind,
} from "@/api/queries";
import { useCan } from "@/auth/member";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { cn, formatDate } from "@/lib/utils";

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

const PLACEHOLDER: Record<TaxonomyKind, string> = {
  stack: "golang",
  concept: "golang.goroutines",
  bucket: "technical",
};

/** The Stacks "current version" column label, with a plain-language explanation that opens when
 * the text is hovered or keyboard-focused (it is focusable, so it is not mouse-only). Written for
 * a non-technical reader — it deliberately avoids "range", "scan job" and "staleness". */
function CurrentVersionHeader() {
  return (
    <span
      tabIndex={0}
      aria-describedby="current-version-help"
      className="group relative inline-block cursor-help underline decoration-dotted underline-offset-4 outline-none"
    >
      current version
      <span
        id="current-version-help"
        role="tooltip"
        className="bg-bank-surface text-bank-ink border-bank-border pointer-events-none invisible absolute top-full left-0 z-20 mt-2 w-72 normal-case no-underline rounded-md border p-3 text-left font-sans text-xs leading-relaxed font-normal tracking-normal opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
      >
        <span className="mb-1 block font-semibold">The newest version people should be using</span>
        Say React is on version 19 today. Type <span className="font-mono">19</span> here.
        <span className="mt-2 block">
          Some questions only make sense for older versions (for example, “React 16 class components”). When
          you run the freshness check, any approved question written for an older version is sent back to
          Review so someone can update it.
        </span>
        <span className="mt-2 block">
          When a new version comes out, change the number here. Nothing is re-checked automatically — run the
          freshness check when you’re ready.
        </span>
        <span className="text-bank-muted mt-2 block">Leave it empty to skip this technology.</span>
      </span>
    </span>
  );
}

/** Same status-chip shape as Badge (ui/badge.tsx), but taxonomy's active/deactivated
 * isn't a DESIGN §6 lifecycle status, so it stays local rather than widening that map. */
function ActiveBadge({ entry }: { entry: TaxonomyEntryOut }) {
  if (!entry.deactivated_at) {
    return (
      <span className="bg-bank-good-wash text-bank-good-ink inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-xs font-medium">
        <span className="bg-bank-good size-1.5 shrink-0 rounded-full" aria-hidden="true" />
        active
      </span>
    );
  }
  return (
    <span
      className="bg-bank-inert-wash text-bank-inert-ink inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-xs font-medium"
      title={`${formatDate(entry.deactivated_at)} · ${entry.deactivated_by ?? "—"} · ${entry.deactivation_reason ?? ""}`}
    >
      <span className="bg-bank-inert size-1.5 shrink-0 rounded-full" aria-hidden="true" />
      deactivated
    </span>
  );
}

function AddRow({ kind, onAdded }: { kind: TaxonomyKind; onAdded: () => void }) {
  const [path, setPath] = useState("");
  const [note, setNote] = useState("");
  const [bucket, setBucket] = useState("");
  const [version, setVersion] = useState("");
  const create = useCreateTaxonomyEntry(kind);
  // §17.2: only a stack belongs to a bucket. Same query key as the Buckets page, so
  // rendering this costs no extra request there.
  const { data: buckets } = useTaxonomy("bucket");

  function add() {
    if (!path.trim()) return;
    create.mutate(
      {
        path,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(bucket ? { bucket } : {}),
        ...(version.trim() ? { current_version: version.trim() } : {}),
      },
      {
        onSuccess: () => {
          setPath("");
          setNote("");
          setBucket("");
          setVersion("");
          onAdded();
        },
      },
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <FieldLabel htmlFor={`${kind}-add-path`}>New {kind}</FieldLabel>
          <Input
            id={`${kind}-add-path`}
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder={PLACEHOLDER[kind]}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
        </div>
        {kind === "stack" && (
          <div className="w-44">
            <FieldLabel htmlFor="stack-add-bucket">Bucket</FieldLabel>
            <Combobox
              id="stack-add-bucket"
              value={bucket}
              onChange={setBucket}
              options={[
                { value: "", label: "unclassified" },
                ...(buckets ?? []).map((b) => ({ value: b.path, label: b.path })),
              ]}
            />
          </div>
        )}
        {kind === "stack" && (
          <div className="w-28">
            <FieldLabel htmlFor="stack-add-version">Current version</FieldLabel>
            <Input
              id="stack-add-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="19"
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
          </div>
        )}
        <div className="flex-1">
          <FieldLabel htmlFor={`${kind}-add-note`}>Note (optional)</FieldLabel>
          <Input
            id={`${kind}-add-note`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
        </div>
        <Button variant="primary" disabled={!path.trim() || create.isPending} onClick={add}>
          {create.isPending ? "Adding…" : "Add"}
        </Button>
      </div>
      <ErrorNote error={create.error} />
    </div>
  );
}

function EditRow({
  entry,
  kind,
  onDone,
}: {
  entry: TaxonomyEntryOut;
  kind: TaxonomyKind;
  onDone: () => void;
}) {
  const [path, setPath] = useState(entry.path);
  const [note, setNote] = useState(entry.note ?? "");
  const [bucket, setBucket] = useState(entry.bucket ?? "");
  const [version, setVersion] = useState(entry.current_version ?? "");
  const update = useUpdateTaxonomyEntry(kind);
  const { data: buckets } = useTaxonomy("bucket");

  function save() {
    if (!path.trim()) return;
    const patch: { id: number; path?: string; note?: string; bucket?: string; current_version?: string } = {
      id: entry.id,
    };
    if (path.trim() !== entry.path) patch.path = path.trim();
    if (note.trim() !== (entry.note ?? "")) patch.note = note.trim();
    if (bucket && bucket !== (entry.bucket ?? "")) patch.bucket = bucket;
    // An empty string is meaningful here — it clears the version — so compare, don't gate on truthiness.
    if (version.trim() !== (entry.current_version ?? "")) patch.current_version = version.trim();
    update.mutate(patch, { onSuccess: onDone });
  }

  return (
    <>
      <td className="px-3 py-2" colSpan={kind === "stack" ? 5 : 3}>
        <div className="flex items-center gap-2">
          <Input value={path} onChange={(e) => setPath(e.target.value)} className="flex-1" autoFocus />
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="note"
            className="flex-1"
          />
          {kind === "stack" && (
            <div className="w-44 shrink-0">
              <Combobox
                value={bucket}
                onChange={setBucket}
                options={[
                  { value: "", label: "unclassified" },
                  ...(buckets ?? []).map((b) => ({ value: b.path, label: b.path })),
                ]}
              />
            </div>
          )}
          {kind === "stack" && (
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="current version"
              className="w-32 shrink-0"
              aria-label="Current version"
            />
          )}
        </div>
        <ErrorNote error={update.error} />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="primary" disabled={update.isPending} onClick={save}>
          Save
        </Button>{" "}
        <Button size="sm" onClick={onDone}>
          Cancel
        </Button>
      </td>
    </>
  );
}

function DeactivateRow({
  entry,
  kind,
  onDone,
}: {
  entry: TaxonomyEntryOut;
  kind: TaxonomyKind;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const deactivate = useDeactivateTaxonomyEntry(kind);

  return (
    <>
      <td className="px-3 py-2" colSpan={kind === "stack" ? 5 : 3}>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="why — recorded on the row"
          autoFocus
        />
        <ErrorNote error={deactivate.error} />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button
          size="sm"
          variant="danger"
          disabled={!reason.trim() || deactivate.isPending}
          onClick={() => deactivate.mutate({ id: entry.id, reason }, { onSuccess: onDone })}
        >
          Confirm
        </Button>{" "}
        <Button size="sm" onClick={onDone}>
          Cancel
        </Button>
      </td>
    </>
  );
}

/** Typing the stack's path is the confirmation: this deletes its questions too and cannot be undone. */
function PurgeStackRow({ entry, onDone }: { entry: TaxonomyEntryOut; onDone: () => void }) {
  const [typed, setTyped] = useState("");
  const purge = usePurgeStack();

  return (
    <>
      <td className="px-3 py-2" colSpan={5}>
        <p className="text-bank-bad-ink mb-1 text-xs">
          Permanently deletes <span className="font-mono">{entry.path}</span> and every question tagged with
          it, with their history. Questions that a golden-pair label points at are kept. This cannot be
          undone.
        </p>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={`type ${entry.path} to confirm`}
          autoFocus
        />
        <ErrorNote error={purge.error} />
        {purge.data && (
          <p className="text-bank-muted mt-1 text-xs">
            Deleted {purge.data.deleted} question{purge.data.deleted === 1 ? "" : "s"}
            {purge.data.skipped_golden > 0
              ? `; ${purge.data.skipped_golden} kept for golden labels, so the stack stays`
              : ""}
            .
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button
          size="sm"
          variant="danger"
          disabled={typed !== entry.path || purge.isPending}
          onClick={() => purge.mutate(entry.id, { onSuccess: (r) => r.skipped_golden === 0 && onDone() })}
        >
          {purge.isPending ? "Deleting…" : "Delete permanently"}
        </Button>{" "}
        <Button size="sm" onClick={onDone}>
          {purge.data ? "Close" : "Cancel"}
        </Button>
      </td>
    </>
  );
}

/**
 * A simple CRUD "master page" for one taxonomy kind — DESIGN's controlled vocabulary
 * for stack_path (kind="stack", a hard gate) or primary_concept (kind="concept", an
 * open one) as ARCHITECTURE §12 database-managed data instead of a config/default.yaml
 * list that needed a deploy to change. Create, rename, and deactivate — never a hard
 * delete (DESIGN §2.3's rule applied here too), and never a cascade to existing
 * questions, which is the one thing worth saying plainly rather than assuming obvious.
 */
export function TaxonomyMasterPage({
  kind,
  title,
  hint,
}: {
  kind: TaxonomyKind;
  title: string;
  hint?: string;
}) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deactivatingId, setDeactivatingId] = useState<number | null>(null);
  const [purgingId, setPurgingId] = useState<number | null>(null);
  const [stackFilter, setStackFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false); // collapsed by default — the list is the page, adding is occasional
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("");
  // Stacks gate every ingest and buckets shape every Engine 2 pick, so this is an Admin's page to
  // change; everyone else reads it.
  const canCreate = useCan("create:taxonomy");
  const canEdit = useCan("edit:taxonomy");
  const canDeactivate = useCan("delete:taxonomy");
  const canPurge = useCan("purge_questions") && kind === "stack";

  const { data: entries, isLoading } = useTaxonomy(kind, includeInactive);
  const { data: buckets } = useTaxonomy("bucket"); // only filtered on for kind="stack"; same query key as the Buckets page
  const { data: stacks } = useTaxonomy("stack"); // only rendered for kind="concept"; same query key as the Stacks page, so no extra request there

  const needle = search.trim().toLowerCase();
  const rows = (entries ?? []).filter(
    (e) =>
      (kind !== "concept" ||
        !stackFilter ||
        e.path === stackFilter ||
        e.path.startsWith(`${stackFilter}.`)) &&
      (kind !== "stack" ||
        !bucketFilter ||
        (bucketFilter === "__none" ? !e.bucket : e.bucket === bucketFilter)) &&
      (!needle || e.path.toLowerCase().includes(needle) || (e.note ?? "").toLowerCase().includes(needle)),
  );

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-base font-semibold tracking-tight">{title}</h1>
        {canCreate && (
          <Button variant={showAdd ? "secondary" : "primary"} onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Close" : `Add a ${kind}`}
          </Button>
        )}
      </div>
      {hint && (
        <Callout>
          {hint} Renaming or deactivating an entry here does not change any question already tagged with it —
          only what new enrichment and edits accept from now on.
        </Callout>
      )}

      {showAdd && canCreate && (
        <Panel>
          <PanelHeader title={`Add a ${kind}`} />
          <div className="p-4">
            <AddRow kind={kind} onAdded={() => setShowAdd(false)} />
          </div>
        </Panel>
      )}

      <Panel className="flex min-h-0 flex-1 flex-col">
        <PanelHeader
          title={`${rows.length} ${kind}${rows.length === 1 ? "" : "s"}`}
          actions={
            <div className="flex items-center gap-3">
              <div className="w-48">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`search ${kind}s…`}
                  aria-label={`Search ${kind}s`}
                />
              </div>
              {kind === "stack" && (
                <div className="w-44">
                  <Combobox
                    value={bucketFilter}
                    onChange={setBucketFilter}
                    options={[
                      { value: "", label: "all buckets" },
                      { value: "__none", label: "unclassified" },
                      ...(buckets ?? []).map((b) => ({ value: b.path, label: b.path })),
                    ]}
                  />
                </div>
              )}
              {kind === "concept" && (
                <div className="w-48">
                  <Combobox
                    value={stackFilter}
                    onChange={setStackFilter}
                    options={[
                      { value: "", label: "all stacks" },
                      ...(stacks ?? []).map((s) => ({ value: s.path, label: s.path })),
                    ]}
                  />
                </div>
              )}
              <label className="text-bank-muted flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                show deactivated
              </label>
            </div>
          }
        />
        <div className="min-h-0 flex-1 overflow-auto">
          {isLoading ? (
            <Empty>Loading…</Empty>
          ) : rows.length === 0 ? (
            <Empty>Nothing here yet.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border text-bank-muted sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>path</th>
                  {kind === "stack" && <th className={TH}>bucket</th>}
                  {kind === "stack" && (
                    <th className={TH}>
                      <CurrentVersionHeader />
                    </th>
                  )}
                  <th className={TH}>note</th>
                  <th className={TH}>added</th>
                  <th className={TH}>status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr
                    key={entry.id}
                    className={cn(
                      "border-bank-border hover:bg-bank-surface-2 border-t align-top transition-colors",
                      entry.deactivated_at && "opacity-60",
                    )}
                  >
                    {editingId === entry.id ? (
                      <EditRow entry={entry} kind={kind} onDone={() => setEditingId(null)} />
                    ) : purgingId === entry.id ? (
                      <PurgeStackRow entry={entry} onDone={() => setPurgingId(null)} />
                    ) : deactivatingId === entry.id ? (
                      <DeactivateRow entry={entry} kind={kind} onDone={() => setDeactivatingId(null)} />
                    ) : (
                      <>
                        <td className="px-3 py-2 font-mono text-[11px]">{entry.path}</td>
                        {kind === "stack" && (
                          <td className="px-3 py-2">
                            {entry.bucket ? (
                              <span className="bg-bank-surface-3 text-bank-ink rounded px-1.5 py-0.5 font-mono text-[11px]">
                                {entry.bucket}
                              </span>
                            ) : (
                              <span className="text-bank-muted font-mono text-[11px]">—</span>
                            )}
                          </td>
                        )}
                        {kind === "stack" && (
                          <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                            {entry.current_version ?? "—"}
                          </td>
                        )}
                        <td className="text-bank-muted px-3 py-2 text-xs">{entry.note ?? "—"}</td>
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                          {formatDate(entry.created_at)}
                          {entry.created_by ? ` · ${entry.created_by}` : ""}
                        </td>
                        <td className="px-3 py-2">
                          <ActiveBadge entry={entry} />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {canPurge && (
                            <Button size="sm" variant="danger" onClick={() => setPurgingId(entry.id)}>
                              Delete permanently
                            </Button>
                          )}{" "}
                          {!entry.deactivated_at && (canEdit || canDeactivate) && (
                            <>
                              {canEdit && (
                                <Button size="sm" onClick={() => setEditingId(entry.id)}>
                                  Edit
                                </Button>
                              )}{" "}
                              {canDeactivate && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => setDeactivatingId(entry.id)}
                                >
                                  Deactivate
                                </Button>
                              )}
                            </>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </div>
  );
}
